import { MS_PER_DAY, formatMonthDay } from '@/helpers/date'

// UTC midnight timestamp of a date / ISO datetime string.
function utcDayStart(value) {
  return new Date(`${String(value).slice(0, 10)}T00:00:00Z`).getTime()
}

// UTC calendar date ("yyyy-mm-dd"). Deliberately NOT helpers/date.js
// dateWithoutTime (viewer-local): the timeline grid is UTC-anchored, and
// locationDateKey reads shifted timestamps through UTC getters.
function utcIsoDate(timestamp) {
  return new Date(timestamp).toISOString().slice(0, 10)
}

// One column per day between the timeline boundaries (inclusive), UTC-based —
// the same convention the backend uses for dateKey grouping.
export function buildTimelineColumns(startDate, endDate) {
  const start = utcDayStart(startDate)
  const daysCount = (utcDayStart(endDate) - start) / MS_PER_DAY + 1
  const todayIso = new Date().toISOString().slice(0, 10)

  return Array.from({ length: daysCount }, (_, i) => {
    const timestamp = start + i * MS_PER_DAY
    const date = new Date(timestamp)
    const iso = utcIsoDate(timestamp)

    return {
      id: i,
      isoDate: iso,
      dateKey: iso.replace(/-/g, ''),
      formattedDate: formatMonthDay(timestamp, 'UTC'),
      day: String(date.getUTCDate()),
      weekend: [0, 6].includes(date.getUTCDay()),
      today: iso === todayIso
    }
  })
}

export function addDays(iso, days) {
  return utcIsoDate(utcDayStart(iso) + days * MS_PER_DAY)
}

// True when the date is in the past (UTC day precision) — such cells are
// frozen: media can't be dragged onto or out of them.
export function isPastDate(value) {
  return new Date(value).toISOString().slice(0, 10) < new Date().toISOString().slice(0, 10)
}

// The media's wall-clock moment in its location's timezone, exposed through
// the Date's UTC getters ("shifted UTC" trick — offset is in seconds).
export function toLocationTime(isoDatetime, tzOffsetSeconds) {
  return new Date(new Date(isoDatetime).getTime() + tzOffsetSeconds * 1000)
}

// Location-local calendar date (YYYYMMDD) — matches the backend's
// scheduled_at.in_time_zone(location_tz).to_date grouping key.
export function locationDateKey(isoDatetime, tzOffsetSeconds) {
  return toLocationTime(isoDatetime, tzOffsetSeconds).toISOString().slice(0, 10).replace(/-/g, '')
}

// Move the media to another calendar date keeping its local wall-clock time:
// combine the target date with the original local time, then shift back to UTC.
export function rescheduleToDate(isoDatetime, targetIsoDate, tzOffsetSeconds) {
  const local = toLocationTime(isoDatetime, tzOffsetSeconds)
  const [year, month, day] = targetIsoDate.split('-').map(Number)
  const combined = Date.UTC(
    year, month - 1, day,
    local.getUTCHours(), local.getUTCMinutes(), local.getUTCSeconds(), local.getUTCMilliseconds()
  )

  return new Date(combined - tzOffsetSeconds * 1000).toISOString()
}
